import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { Pagination } from "./Pagination";
import { TableSkeleton } from "./LoadingSkeleton";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";

// ─────────────────────────────────────────────
// DataTable
//
// Reusable data table with sortable headers, pagination,
// loading skeleton, empty state, row click, and row actions.
// Design: §6.7 Data Display, §9.3 Table Pattern
// ─────────────────────────────────────────────

export interface ColumnDef<T> {
  key: string;
  header: string;
  /** Render function for the cell. Receives the row data. */
  cell: (row: T) => React.ReactNode;
  /** Whether this column is sortable (default false) */
  sortable?: boolean;
  /** Optional fixed/min width class or px value */
  width?: string;
  /** Optional accessor used for client-side sorting. Defaults to row[key]. */
  sortAccessor?: (row: T) => string | number | Date | null | undefined;
}

export interface SortState {
  key: string;
  direction: "asc" | "desc";
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  /** Called when a row is clicked */
  onRowClick?: (row: T) => void;
  /** Optional render for action cell (last column) */
  renderRowActions?: (row: T) => React.ReactNode;
  /** Loading state — shows skeleton */
  isLoading?: boolean;
  /** Query-error state — shows an error message + Retry instead of the empty state (WP20 S-147) */
  isError?: boolean;
  /** Retry handler (typically the query's refetch); renders a Retry button when set */
  onRetry?: () => void;
  /** Empty state configuration */
  emptyState?: {
    title: string;
    description?: string;
    icon?: React.FC<{ className?: string }>;
    action?: { label: string; onClick: () => void };
  };
  /** Controlled sort state */
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  /** Pagination */
  page?: number;
  totalPages?: number;
  totalItems?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  renderRowActions,
  isLoading,
  isError,
  onRetry,
  emptyState,
  sort,
  onSortChange,
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  className,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [internalSort, setInternalSort] = useState<SortState | undefined>();

  const activeSort = sort ?? internalSort;

  function handleSort(key: string) {
    const newSort: SortState =
      activeSort?.key === key
        ? { key, direction: activeSort.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" };

    if (onSortChange) {
      onSortChange(newSort);
    } else {
      setInternalSort(newSort);
    }
  }

  const allColumns = renderRowActions
    ? [...columns, { key: "__actions__", header: "", cell: renderRowActions, sortable: false, width: "w-24" } as ColumnDef<T>]
    : columns;

  // Client-side sorting (uncontrolled). When the parent provides `sort`,
  // we assume it handles ordering server-side and don't re-sort.
  const sortedData = useMemo(() => {
    if (sort || !internalSort) return data;
    const col = columns.find((c) => c.key === internalSort.key);
    if (!col) return data;
    const accessor =
      col.sortAccessor ??
      ((row: T) => (row as Record<string, unknown>)[internalSort.key] as
        | string
        | number
        | Date
        | null
        | undefined);
    const dir = internalSort.direction === "asc" ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * dir;
      }
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv), undefined, {
        sensitivity: "base",
        numeric: true,
      }) * dir;
    });
  }, [data, sort, internalSort, columns]);

  return (
    <div className={cn("overflow-hidden rounded-card border border-border-default bg-card", className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-body">
          {/* Header */}
          <thead>
            <tr className="border-b border-border-default">
              {allColumns.map((col) => {
                const isSorted = activeSort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    aria-sort={
                      col.sortable
                        ? isSorted
                          ? activeSort?.direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                        : undefined
                    }
                    className={cn(
                      "px-5 py-3 text-left text-table-header font-medium tracking-table-header text-text-muted",
                      col.width
                    )}
                  >
                    {col.sortable ? (
                      // Real <button> so the sort control is keyboard-focusable and
                      // operable with Enter/Space; aria-sort (on the th) exposes state.
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        aria-label={`${t("common.sortBy")} ${col.header}`}
                        className="-ml-1 flex items-center gap-1 rounded-control px-1 uppercase select-none hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      >
                        <span>{col.header}</span>
                        <span className="text-text-muted" aria-hidden="true">
                          {isSorted ? (
                            activeSort?.direction === "asc" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowDown className="h-3 w-3" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-40" />
                          )}
                        </span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-1">
                        <span className="uppercase">{col.header}</span>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={allColumns.length} className="p-0">
                  <TableSkeleton rows={6} columns={allColumns.length} />
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={allColumns.length}>
                  <ErrorState onRetry={onRetry} />
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={allColumns.length}>
                  {emptyState ? (
                    <EmptyState
                      icon={emptyState.icon}
                      title={emptyState.title}
                      description={emptyState.description}
                      action={emptyState.action}
                    />
                  ) : (
                    <div className="py-12 text-center text-body text-text-muted">
                      {t("common.noData")}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  className={cn(
                    "border-b border-border-subtle last:border-0",
                    "odd:bg-transparent even:bg-[var(--zebra-stripe)]",
                    onRowClick &&
                      "cursor-pointer hover:bg-hover focus-visible:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // Keyboard access for clickable rows. Row semantics are kept
                  // (no role="button") so screen-reader table navigation still
                  // works; the row is focusable and activates on Enter/Space.
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {allColumns.map((col) => (
                    <td key={col.key} className={cn("px-5 py-3.5 text-body text-text-secondary", col.width)}>
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {!isLoading && !isError && page != null && totalPages != null && totalPages > 0 && onPageChange && (
        <div className="border-t border-border-subtle px-5 py-3">
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
}
